import { Grid, TextField, MenuItem, Typography } from "@mui/material";
import React, { useCallback, useContext } from "react";
import { CourseStructureCtx, DbUser } from "../../../../interfaces";
import { Role } from "../../../../enums";
import { SchoolSelect } from "../utils/SchoolSelect";
import { TeacherSelect } from "../utils/TeacherSelect";
import { UserManagementModal } from "../UserManagementModal";
import axios from "../../../../services/axios";
import { AlertState } from "../../../../types";
import { TechnicalIssuesMessage } from "../../utils/Toast";
import { AxiosError } from "axios";
import {
  ClassCreationState,
  getError,
  initClassCreationState,
  useClassCreationState,
} from "../state/useClassCreationState";
import { formatISO } from "date-fns";
import { Class } from "@backend-types";
import { makeStyles } from "@mui/styles";
import { v2theme } from "../../../../styles/Theme";
import { formatDate, generateGtmEvent } from "../../../../Helpers";
import { CourseStructureContext } from "../../../../context/CourseStructureContext";
import { ClassDetailEditableItem } from "../../../v3/insights/classSettings/ClassDetails";

export type AddClassProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (alert: AlertState) => void;
  currentUser: DbUser;
};

type ClassRequest = Pick<
  Class,
  "end_date" | "period_block" | "course_id" | "teacher_id" | "start_date"
>;

const useStyle = makeStyles((theme) => ({
  gridItem: {
    padding: "0px !important",
  },
  inputText: {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
    },
  },
}));
const HandleDataLayer = (
  event: string,
  action: string,
  name: string,
  type: string,
  region: string,
  section: string,
  text: string,
  metaData?: any
) => {
  generateGtmEvent({ event, action, name, type, region, section, text }, metaData);
};

export function AddClass(props: AddClassProps) {
  const [state, dispatch] = useClassCreationState(props);
  const classes = useStyle();
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  // limit selection to available courses based on role
  let selectableCourses =
    props.currentUser.role === Role.super
      ? courseStructureCtx.allCourses
      : courseStructureCtx.allCourses.filter(
          (item) => !item.metadata?.management_page?.super_admins_only
        );

  selectableCourses = selectableCourses.sort((a, b) => {
    const courseA = a.name?.toLowerCase() ?? "";
    const courseB = b.name?.toLowerCase() ?? "";
    return courseA.localeCompare(courseB);
  });
  const updateForm = useCallback(
    (update: Partial<ClassCreationState["form"]>) =>
      dispatch({ type: "updateForm", update }),
    [dispatch]
  );

  const submit = async () => {
    HandleDataLayer(
      "form",
      "click",
      "onclick",
      "click",
      "main content",
      "create class",
      "save"
    );
    const { form } = state;
    const course = courseStructureCtx.allCourses.find(
      (item) => item.id === form.courseId
    );
    const className = `${course?.name} ${form.period} ${
      form.startDate ? new Date(form.startDate).toLocaleDateString() : ""
    }`;

    try {
      await axios.post<ClassRequest>("/class", {
        teacher_id: form.teacherId,
        course_id: form.courseId,
        course_name: course?.name,
        period_block: form.period,
        start_date: formatISO(form.startDate!),
        end_date: formatISO(form.endDate!),
        school_id: Number(form.schoolId),
      });
      const selectedSchool = state.data.schools.value.find(
        (item) => item.id === Number(form.schoolId)
      );
      const selectedTeacher = state.data.teachers.value.find(
        (item) => item.id === form.teacherId
      );
      HandleDataLayer(
        "form",
        "creating a class",
        "onsubmit",
        "submit",
        "main content",
        "create class",
        "class created",
        {
          school: `${selectedSchool ? selectedSchool.name : ""}`,
          teacher: `${selectedTeacher ? selectedTeacher.full_name : ""}`,
          course: course?.name,
          "period/block": form.period,
          start_date: form.startDate ? formatDate(new Date(form.startDate)) : "",
          end_date: form.endDate ? formatDate(new Date(form.endDate)) : "",
        }
      );
      props.onSubmit({
        severity: "success",
        message: `Created ${className}`,
      });
      dispatch({
        type: "replaceState",
        state: initClassCreationState(props.currentUser, state.data),
      });
    } catch (e: any) {
      if (e.response.status === 401) {
        props.onSubmit({
          severity: "error",
          message:
            "The class you are trying to edit belongs to another school. Please try another class.",
        });
      }
      if (e.response.status === 404) {
        props.onSubmit({
          severity: "error",
          message:
            "The teacher being linked to the class does not or no longer exists. Please try linking another teacher.",
        });
      }
      if (
        e instanceof AxiosError &&
        typeof e.response?.data === "string" &&
        e.response?.data.includes("DuplicateClassNameException")
      ) {
        props.onSubmit({
          severity: "error",
          message: `${className} already exists. Please use another name.`,
        });
      } else {
        props.onSubmit({
          severity: "error",
          message: TechnicalIssuesMessage,
        });
      }
      throw e;
    }
  };

  const handleClose = () => {
    props.onClose();
  };

  return (
    <UserManagementModal
      title="Create class"
      open={props.open}
      onClose={handleClose}
      onSave={submit}
      errorText={getError(state)}>
      <Typography color="textSecondary" gutterBottom>
        For the class name, please select a unique combination of subject, period/block,
        and start date. A duplicate class name cannot be created.
      </Typography>
      <Typography color="textSecondary" sx={{ marginBottom: "32px" }}>
        The school and teacher must exist before creating a class.
      </Typography>
      <Grid container spacing={6}>
        <Grid item xs={6}>
          <SchoolSelect
            schoolId={state.form.schoolId}
            onChange={(schoolId) => {
              updateForm({ schoolId });
              let schoolName = state.data.schools.value.find(
                (item) => item.id === Number(schoolId)
              );
              HandleDataLayer(
                "select",
                "click",
                "onclick",
                "select a school",
                "main content",
                "create class ^ school",
                schoolName?.name!
              );
            }}
            options={state.data.schools.value}
            required={true}
            disabled={props.currentUser.role !== Role.super}
          />
        </Grid>
        <Grid item xs={6}>
          <TeacherSelect
            teacherId={state.form.teacherId}
            onChange={(teacherId) => {
              updateForm({ teacherId });
              let teacherName = state.data.teachers.value.find(
                (item) => item.id === teacherId
              );
              HandleDataLayer(
                "select",
                "click",
                "onclick",
                "select a teacher",
                "main content",
                "create class ^ teacher",
                teacherName?.full_name!
              );
            }}
            disabled={props.currentUser.role === Role.teacher || !state.form.schoolId}
            required={true}
            options={state.data.teachers.value}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            className={classes.inputText}
            required
            select
            label="Course"
            type="number"
            value={state.form.courseId}
            onChange={(e) => {
              updateForm({ courseId: Number(e.target.value) });
              HandleDataLayer(
                "select",
                "click",
                "onclick",
                "select a course",
                "main content",
                "create class ^ course",
                courseStructureCtx.allCourses.find(
                  (item) => item.id === Number(e.target.value)
                )?.name ?? ""
              );
            }}
            fullWidth>
            {selectableCourses.map((item, index) => {
              return (
                <MenuItem key={`subject-${index}-${item.name}-new`} value={item.id}>
                  {item.name}
                </MenuItem>
              );
            })}
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            className={classes.inputText}
            required
            fullWidth
            label="Period/Block"
            value={state.form.period}
            onChange={(period) => {
              updateForm({ period: period.target.value });
              HandleDataLayer(
                "form",
                "click",
                "onclick",
                "blur",
                "main content",
                "create class ^ period/block",
                period.target.value
              );
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <ClassDetailEditableItem
            variant={"datepicker"}
            value={state.form.startDate && formatDate(new Date(state.form.startDate))}
            label={"Start Date"}
            placeholder={"Start date"}
            isVisible={true}
            onChange={(e) => {
              updateForm({ startDate: new Date(e) });
            }}
            disabled={false}
          />
        </Grid>
        <Grid item xs={6}>
          <ClassDetailEditableItem
            variant={"datepicker"}
            value={state.form.endDate && formatDate(new Date(state.form.endDate))}
            label={"End Date"}
            placeholder={"End date"}
            isVisible={true}
            onChange={(e) => {
              updateForm({ endDate: new Date(e) });
            }}
            disabled={false}
          />
        </Grid>
      </Grid>
    </UserManagementModal>
  );
}
